import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShoppingCart, Plus } from 'lucide-react'
import { cn, stripHtml } from '@/lib/utils'
import { filterTypeLabels } from '@/hooks/useFilters'
import type { Product } from '@/types'

interface ProductCardProps {
  product: Product
  categorySlug?: string
}

export function ProductCard({ product, categorySlug }: ProductCardProps) {
  const sortedImages = [...(product.images || [])].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1
    if (!a.is_primary && b.is_primary) return 1
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    return a.id.localeCompare(b.id)
  })
  const primaryImage = sortedImages[0]
  const secondaryImage = sortedImages[1]
  const hasSecondImage = !!secondaryImage

  const productUrl = categorySlug
    ? `/products/${categorySlug}/${product.slug}`
    : `/products/${product.category?.slug}/${product.slug}`

  // Get filter badges with consistent ordering (using filterTypeLabels order)
  const filterOrder = Object.keys(filterTypeLabels)
  const badges = [...(product.filters || [])]
    .sort((a, b) => {
      const aIndex = filterOrder.indexOf(a.filter_type)
      const bIndex = filterOrder.indexOf(b.filter_type)
      // Unknown types go to end
      const aOrder = aIndex === -1 ? filterOrder.length : aIndex
      const bOrder = bIndex === -1 ? filterOrder.length : bIndex
      return aOrder - bOrder
    })
    .map((f) => ({
      type: f.filter_type,
      value: f.filter_value,
    }))

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={productUrl} className="group block card-hover h-full">
        <div className="flex flex-col h-full">
          {/* Image with Hover Effect */}
          <div className="aspect-[4/3] bg-white relative overflow-hidden">
            {primaryImage ? (
              <div className="relative w-full h-full">
                {/* Primary Image */}
                <img
                  src={primaryImage.image_url}
                  alt={primaryImage.alt_text || product.name}
                  loading="lazy"
                  className={cn(
                    "absolute inset-0 w-full h-full object-contain transition-opacity duration-300",
                    hasSecondImage && "group-hover:opacity-0"
                  )}
                />
                {/* Secondary Image - shown on hover */}
                {hasSecondImage && (
                  <img
                    src={secondaryImage.image_url}
                    alt={secondaryImage.alt_text || product.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ShoppingCart className="w-12 h-12 text-secondary-300" />
              </div>
            )}

            {/* Quick Add Button */}
            <button
              onClick={(e) => {
                e.preventDefault()
                // Will navigate to detail page for configuration
              }}
              className="absolute bottom-3 right-3 p-2 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary-50 z-10"
              title="View product"
            >
              <Plus className="w-5 h-5 text-primary-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 flex-1 flex flex-col">
            {/* Product Code */}
            <span className="text-xs font-mono text-secondary-400 mb-1">
              {product.code}
            </span>

            {/* Name */}
            <h3 className="font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
              {product.name}
            </h3>

            {/* Description */}
            {product.description && (
              <p className="text-sm text-secondary-500 line-clamp-2 mb-3 flex-1">
                {stripHtml(product.description)}
              </p>
            )}

            {/* Badges */}
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {badges.map((badge, idx) => (
                  <span
                    key={idx}
                    className={cn(
                      'badge text-xs',
                      badge.type === 'fire_class' && 'bg-red-100 text-red-700',
                      badge.type === 'water_tightness' && 'bg-blue-100 text-blue-700',
                      badge.type === 'material' && 'bg-gray-100 text-gray-700',
                      badge.type === 'product' && 'bg-purple-100 text-purple-700'
                    )}
                  >
                    {badge.value.toUpperCase()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  )
}
